import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import { AuctionPhase } from '../components/auction';
import TeamsPhase from '../components/teams/TeamsPhase';
import { FixturesPhase } from '../components/fixtures';
import { LivePhase } from '../components/live-scoring';
import { ResultsPhase } from '../components/results';
import { RulesPhase } from '../components/rulebook';

// Phase icons mapping
const PHASE_ICONS = {
  overview: 'ph-info',
  rules: 'ph-book-open',
  register: 'ph-clipboard-text',
  teams: 'ph-users-three',
  auction: 'ph-gavel',
  fixtures: 'ph-list-numbers',
  live: 'ph-broadcast',
  results: 'ph-trophy',
};

// ── Phase tab strip with edge-fade affordance to hint that more tabs exist ──
function ScrollableTabs({ phases, activePhase, handlePhaseChange }) {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const recompute = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setEdges({
      left: scrollLeft > 4,
      right: scrollLeft + clientWidth < scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    recompute();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [recompute, phases.length]);

  // Auto-scroll the active tab into view so it's never hidden by the fade
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !activePhase) return;
    const btn = el.querySelector(`[data-testid="phase-tab-${activePhase.phase_key}"]`);
    if (btn && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activePhase]);

  return (
    <div className="relative">
      <div ref={scrollerRef} className="flex overflow-x-auto no-scrollbar">
        {phases.map((phase) => {
          const isActive = activePhase?.phase_key === phase.phase_key;
          const isComingSoon = phase.status === 'coming_soon';
          const iconClass = PHASE_ICONS[phase.phase_key] || 'ph-circle';

          return (
            <button
              key={phase.phase_key}
              onClick={() => handlePhaseChange(phase)}
              className={`flex-shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-bold border-b-2 transition-all ${
                isActive
                  ? 'border-[#E54562] text-[#E54562] bg-[#E54562]/5'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`phase-tab-${phase.phase_key}`}
            >
              <i className={`ph-bold ${iconClass}`}></i>
              <span>{phase.title || phase.default_title}</span>
              {isComingSoon && (
                <span className="text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Left edge fade + chevron — appears once user scrolls right */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute left-0 top-0 bottom-0 w-10 flex items-center justify-start pl-1 bg-gradient-to-r from-white via-white to-transparent transition-opacity duration-150 ${
          edges.left ? 'opacity-100' : 'opacity-0'
        }`}
        data-testid="phase-tabs-fade-left"
      >
        <i className="ph-bold ph-caret-left text-gray-400 text-sm"></i>
      </div>

      {/* Right edge fade + chevron — hints at more tabs */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-10 flex items-center justify-end pr-1 bg-gradient-to-l from-white via-white to-transparent transition-opacity duration-150 ${
          edges.right ? 'opacity-100' : 'opacity-0'
        }`}
        data-testid="phase-tabs-fade-right"
      >
        <i className="ph-bold ph-caret-right text-gray-400 text-sm"></i>
      </div>
    </div>
  );
}

// Status colors
const STATUS_COLORS = {
  disabled: 'bg-gray-100 text-gray-400',
  coming_soon: 'bg-amber-100 text-amber-600',
  active: 'bg-green-100 text-green-600',
  completed: 'bg-blue-100 text-blue-600',
};

// The Rules tab isn't a configurable phase: it shows for the event's admins, and
// for everyone once a rulebook is published. It sits right after Overview.
const RULES_PHASE = { phase_key: 'rules', title: 'Rules', default_title: 'Rules', status: 'active' };
function withRulesTab(phases, event, rulebook) {
  const show = event?.can_manage || (rulebook?.exists && rulebook?.live);
  if (!show) return phases;
  const at = phases.findIndex((p) => p.phase_key === 'overview');
  const out = [...phases];
  out.splice(at >= 0 ? at + 1 : 0, 0, RULES_PHASE);
  return out;
}

const patternStripes = {
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.12) 10px, rgba(255,255,255,0.12) 20px)',
};

export default function TournamentDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [phases, setPhases] = useState([]);
  const [activePhase, setActivePhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // This event's admins (owner, co-admins, Super Admins) — set by GET /events/{id}
  const isAdmin = !!event?.can_manage;

  const headerRef = useRef(null);
  const heroTitleRef = useRef(null);
  const [headerH, setHeaderH] = useState(64);
  const [compactHeader, setCompactHeader] = useState(false);

  // The fixed header's height depends on the phone's safe area; measure it so
  // the tab bar sticks right under it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setHeaderH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Once the hero's title has scrolled under the header, show a compact
  // thumbnail + title in the header so people keep their bearings.
  useEffect(() => {
    const el = heroTitleRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setCompactHeader(!entry.isIntersecting),
      { rootMargin: `-${headerH}px 0px 0px 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, headerH]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load user from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setCurrentUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
      
      const [eventRes, phasesRes, rulebookRes] = await Promise.all([
        axiosInstance.get(`/events/${id}`),
        axiosInstance.get(`/v1/tournaments/${id}/phases`),
        axiosInstance.get(`/v1/events/${id}/rulebook`).catch(() => ({ data: null })),
      ]);
      const allPhases = withRulesTab(phasesRes.data, eventRes.data, rulebookRes.data);

      setEvent(eventRes.data);
      setPhases(allPhases);

      // Set active phase from URL or default to first
      if (allPhases.length > 0) {
        const urlPhaseKey = searchParams.get('phase');
        const phaseFromUrl = allPhases.find(p => p.phase_key === urlPhaseKey);
        if (phaseFromUrl) {
          setActivePhase(phaseFromUrl);
        } else {
          // Default to first phase
          setActivePhase(allPhases[0]);
          setSearchParams({ phase: allPhases[0].phase_key });
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load tournament');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, setSearchParams, searchParams]);

  // Only load data on initial mount or when event ID changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle phase change from URL without reloading data
  useEffect(() => {
    if (phases.length > 0) {
      const urlPhaseKey = searchParams.get('phase');
      const phaseFromUrl = phases.find(p => p.phase_key === urlPhaseKey);
      if (phaseFromUrl && phaseFromUrl.phase_key !== activePhase?.phase_key) {
        setActivePhase(phaseFromUrl);
      }
    }
  }, [searchParams, phases, activePhase?.phase_key]);

  const handlePhaseChange = (phase) => {
    setActivePhase(phase);
    setSearchParams({ phase: phase.phase_key });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  return (
    <div
      // pb-nav: just enough room to clear the bottom nav, so a short tab can't
      // scroll far enough to push the hero away and leave a blank area.
      className="bg-gray-50 min-h-screen pb-nav text-[#2B3A67]"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* Header */}
      <header
        ref={headerRef}
        className={`fixed top-0 z-50 w-full pt-safe transition-colors duration-200 ${
          compactHeader ? 'bg-[#2B3A67] shadow-md' : 'bg-[#2B3A67]/80 backdrop-blur-md'
        }`}
        data-testid="tournament-header"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate('/events')}
            className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-gray-100"
            data-testid="tournament-back-button"
          >
            <i className="ph-bold ph-arrow-left text-xl text-[#2B3A67]"></i>
          </button>
          <div
            className={`flex-1 min-w-0 flex items-center gap-2.5 transition-all duration-200 ${
              compactHeader ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'
            }`}
            aria-hidden={!compactHeader}
            data-testid="tournament-compact-title"
          >
            <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden bg-[#F4D341] ring-1 ring-white/30 flex items-center justify-center">
              {event.image_url ? (
                <img src={event.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <i className="ph-fill ph-trophy text-base text-[#2B3A67]"></i>
              )}
            </div>
            <p className="min-w-0 truncate text-sm font-extrabold text-white">{event.title}</p>
          </div>
          <div className="flex gap-2">
            <button className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-gray-100">
              <i className="ph-bold ph-share-network text-xl text-[#2B3A67]"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="h-[280px] w-full relative overflow-hidden" style={{ backgroundColor: '#2B3A67' }}>
        <div className="absolute inset-0 opacity-20" style={patternStripes}></div>
        {event.image_url && (
          <img src={event.image_url} alt={event.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full border-8 border-white/10" style={{ backgroundColor: '#F4D341' }}></div>
        <div className="absolute inset-0 flex items-center justify-center pt-12">
          <i className="ph-duotone ph-trophy text-[140px] text-white opacity-30"></i>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent"></div>
        
        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-4">
          <div className="flex gap-2 mb-2">
            <span className="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest bg-[#F4D341] text-[#2B3A67]">
              Tournament
            </span>
            <span className="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest bg-white/20 text-white">
              {event.type}
            </span>
          </div>
          <h1 ref={heroTitleRef} className="text-2xl font-extrabold text-white leading-tight" data-testid="tournament-title">
            {event.title}
          </h1>
        </div>
      </div>

      {/* Phase Tabs */}
      {phases.length > 0 && (
        <div className="sticky z-40 bg-white shadow-sm border-b border-gray-100" style={{ top: headerH }} data-testid="phase-tabs">
          <ScrollableTabs phases={phases} activePhase={activePhase} handlePhaseChange={handlePhaseChange} />
        </div>
      )}

      {/* Phase Content */}
      <main className="px-4 pt-3 pb-6">
        {activePhase ? (
          <PhaseContent 
            phase={activePhase} 
            event={event} 
            navigate={navigate}
            formatDate={formatDate}
            isAdmin={isAdmin}
          />
        ) : (
          <div className="text-center py-12 text-gray-500">
            No phases configured for this tournament.
          </div>
        )}
      </main>
    </div>
  );
}

// Phase Content Component
function PhaseContent({ phase, event, navigate, formatDate, isAdmin }) {
  const isComingSoon = phase.status === 'coming_soon';
  const iconClass = PHASE_ICONS[phase.phase_key] || 'ph-circle';

  // Admins can bypass time-gating - they see the full phase content
  // even if it's "coming_soon", with a banner indicating the status
  const showAdminBypass = isComingSoon && isAdmin && phase.admin_can_access;

  // Coming Soon State (only for non-admin users)
  if (isComingSoon && !showAdminBypass) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-testid="phase-coming-soon">
        <div className="w-20 h-20 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
          <i className={`ph-bold ${iconClass} text-4xl text-amber-600`}></i>
        </div>
        <h2 className="text-xl font-extrabold mb-2">{phase.title || phase.default_title}</h2>
        <p className="text-gray-500 mb-4">Coming Soon</p>
        {phase.start_date && (
          <div className="inline-flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-xl">
            <i className="ph-bold ph-calendar text-amber-600"></i>
            <span className="text-sm font-bold text-amber-700">Opens {formatDate(phase.start_date)}</span>
          </div>
        )}
      </div>
    );
  }

  // Render phase-specific content (with optional admin pre-access banner)
  return (
    <div className="space-y-4">
      {/* Admin Pre-Access Banner */}
      {showAdminBypass && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200" data-testid="admin-pre-access-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <i className="ph-bold ph-shield-star text-amber-600"></i>
            </div>
            <div className="flex-1">
              <p className="font-bold text-amber-800">Admin Pre-Access Mode</p>
              <p className="text-sm text-amber-600">
                This phase opens to participants on {formatDate(phase.start_date)}. 
                You have early access for configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actual Phase Content */}
      {renderPhaseContent(phase, event, navigate, formatDate)}
    </div>
  );
}

// Helper to render phase-specific content
function renderPhaseContent(phase, event, navigate, formatDate) {
  switch (phase.phase_key) {
    case 'overview':
      return <OverviewPhase phase={phase} event={event} formatDate={formatDate} />;
    case 'rules':
      return <RulesPhase event={event} />;
    case 'register':
      return <RegisterPhase phase={phase} event={event} navigate={navigate} />;
    case 'teams':
      return <TeamsPhase event={event} />;
    case 'auction':
      return <AuctionPhaseWrapper phase={phase} event={event} />;
    case 'fixtures':
      return <FixturesPhase phase={phase} event={event} />;
    case 'live':
      return <LivePhase phase={phase} event={event} />;
    case 'results':
      return <ResultsPhase phase={phase} event={event} />;
    default:
      return <GenericPhase phase={phase} />;
  }
}

const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;

// Who can register in the app, and what it costs
function RegistrationFees({ config }) {
  const roles = config.registration_roles || ['player', 'team_owner', 'sponsor'];
  const rows = [];
  if (config.team_registration) {
    rows.push(['Team', config.team_fee > 0
      ? `${inr(config.team_fee)}${config.player_fee > 0 ? ` or ${inr(config.player_fee)} per player` : ''}`
      : (config.player_fee > 0 ? `${inr(config.player_fee)} per player` : 'Free')]);
  } else {
    if (roles.includes('player')) rows.push(['Player', config.player_fee > 0 ? inr(config.player_fee) : 'Free']);
    if (roles.includes('team_owner')) rows.push(['Team owner', config.team_owner_fee > 0 ? inr(config.team_owner_fee) : 'Free']);
  }
  if (roles.includes('sponsor')) rows.push(['Sponsor', config.sponsor_fee > 0 ? inr(config.sponsor_fee) : 'Free']);
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="overview-registration">
      <h3 className="font-bold mb-3 flex items-center gap-2">
        <i className="ph-bold ph-clipboard-text text-[#3AAFA9]"></i>
        Registration
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">Registration isn't open in the app for this tournament.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {rows.map(([label, price]) => (
            <div key={label} className="flex justify-between py-2 text-sm">
              <span className="text-gray-600">{label}</span>
              <span className={`font-bold ${price === 'Free' ? 'text-green-600' : 'text-[#2B3A67]'}`}>{price}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Overview Phase
function OverviewPhase({ phase, event, formatDate }) {
  return (
    <div className="space-y-6" data-testid="overview-phase">
      {/* Event Info Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-extrabold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-info text-[#3AAFA9]"></i>
          About the Tournament
        </h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          {phase.description || phase.default_description || event.description}
        </p>
        
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Start Date</p>
            <p className="font-bold">{formatDate(event.start_time)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">End Date</p>
            <p className="font-bold">{formatDate(event.end_time)}</p>
          </div>
        </div>
      </div>

      {/* Venue Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <i className="ph-bold ph-map-pin text-[#E54562]"></i>
          Venue
        </h3>
        <p className="font-bold">{event.venue?.name}</p>
        <p className="text-sm text-gray-500">{event.venue?.address}, {event.venue?.city}</p>
      </div>

      {/* Cricket Config */}
      {event.cricket_config && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <i className="ph-bold ph-cricket text-[#F4D341]"></i>
            Tournament Format
          </h3>
          <div className="flex flex-wrap gap-2">
            {event.cricket_config.format_label && (
              <span className="bg-[#2B3A67]/10 text-[#2B3A67] px-3 py-1.5 rounded-full text-sm font-bold">
                {event.cricket_config.format_label}
              </span>
            )}
            {event.cricket_config.ball_type_label && (
              <span className="bg-[#3AAFA9]/10 text-[#3AAFA9] px-3 py-1.5 rounded-full text-sm font-bold">
                {event.cricket_config.ball_type_label}
              </span>
            )}
            {event.cricket_config.category_label && (
              <span className="bg-[#E54562]/10 text-[#E54562] px-3 py-1.5 rounded-full text-sm font-bold">
                {event.cricket_config.category_label}
              </span>
            )}
          </div>
          {(event.cricket_config.max_teams || event.cricket_config.max_players) && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              {event.cricket_config.max_teams && (
                <div className="text-center bg-gray-50 rounded-xl p-3">
                  <p className="text-2xl font-extrabold text-[#2B3A67]">{event.cricket_config.max_teams}</p>
                  <p className="text-xs text-gray-500 font-bold">Max Teams</p>
                </div>
              )}
              {event.cricket_config.max_players && (
                <div className="text-center bg-gray-50 rounded-xl p-3">
                  <p className="text-2xl font-extrabold text-[#2B3A67]">{event.cricket_config.max_players}</p>
                  <p className="text-xs text-gray-500 font-bold">Max Players</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Registration & fees — only what the organiser opened in the app */}
      {event.cricket_config && <RegistrationFees config={event.cricket_config} />}

      {/* Rules PDF */}
      {phase.rules_pdf_url && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <i className="ph-bold ph-file-pdf text-red-500"></i>
            {phase.phase_key === 'overview' ? 'Event Rules' : 'Phase Rules'}
          </h3>
          <button
            onClick={() => {
              // Force download the PDF by creating a temporary link
              const link = document.createElement('a');
              link.href = phase.rules_pdf_url;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              // For Cloudinary raw files, append fl_attachment to force download
              if (phase.rules_pdf_url.includes('cloudinary.com') && phase.rules_pdf_url.includes('/raw/')) {
                const url = new URL(phase.rules_pdf_url);
                // Add fl_attachment parameter
                link.href = phase.rules_pdf_url.replace('/upload/', '/upload/fl_attachment/');
              }
              link.click();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B3A67] text-white rounded-lg text-sm font-semibold hover:bg-[#1e2a4a] transition-colors"
            data-testid={`phase-rules-pdf-link-${phase.phase_key}`}
          >
            <i className="ph-bold ph-download-simple"></i>
            Download PDF
          </button>
        </div>
      )}
    </div>
  );
}

// Register Phase
// "Player", "Player or Team Owner", "Player, Team Owner or Sponsor"
const ROLE_NAMES = { player: 'Player', team_owner: 'Team Owner', sponsor: 'Sponsor' };
function listRoles(roles) {
  const names = roles.map((r) => ROLE_NAMES[r] || r);
  return names.length <= 1 ? (names[0] || '') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

function RegisterPhase({ phase, event, navigate }) {
  const [regInfo, setRegInfo] = useState(null);

  useEffect(() => {
    axiosInstance.get(`/v1/events/${event.id}/registration-info`)
      .then(r => setRegInfo(r.data))
      .catch(() => {});
  }, [event.id]);

  const myRoles = regInfo?.my_roles || {};

  const ROLE_LABELS  = { player: 'Player', team_owner: 'Team Owner', sponsor: 'Sponsor' };
  const ROLE_COLORS  = {
    player:     'bg-emerald-100 text-emerald-700 border-emerald-200',
    team_owner: 'bg-purple-100  text-purple-700  border-purple-200',
    sponsor:    'bg-orange-100  text-orange-700  border-orange-200',
  };
  const STATUS_LABELS = {
    approved:                 'Approved',
    pending:                  'Pending Review',
    approved_pending_payment: 'Awaiting Payment',
    rejected:                 'Rejected',
    draft:                    'Draft — incomplete',
  };
  const STATUS_DOT = {
    approved:                 'bg-green-500',
    pending:                  'bg-amber-500',
    approved_pending_payment: 'bg-blue-500',
    rejected:                 'bg-red-500',
    draft:                    'bg-gray-400',
  };

  // "Bring your own team" tournaments: people register whole teams (players
  // join through the team's invite link), plus sponsors.
  const byo = regInfo?.team_formation_method === 'team_registration';
  // Only the registrations the organiser opened in the app
  const offered = regInfo?.roles_offered || ['player', 'team_owner', 'sponsor'];
  const ALL_ROLES = (byo ? ['sponsor'] : ['player', 'team_owner', 'sponsor']).filter((r) => offered.includes(r));
  const nothingOpen = !byo && ALL_ROLES.length === 0;
  // Split into active (non-draft) and draft
  const activeRegistrations = Object.entries(myRoles).filter(([, s]) => s !== 'draft');
  const draftRegistrations   = Object.entries(myRoles).filter(([, s]) => s === 'draft');
  const registeredActiveRoles = activeRegistrations.map(([r]) => r);
  const unregisteredRoles = ALL_ROLES.filter(r => !registeredActiveRoles.includes(r));

  const hasActive   = activeRegistrations.length > 0;
  const hasDrafts   = draftRegistrations.length > 0;
  const canRegMore  = byo || unregisteredRoles.length > 0;   // a team can always be registered
  const fullyDone   = hasActive && !canRegMore;

  return (
    <div className="space-y-4" data-testid="register-phase">

      {/* ── My Registrations Banner ── */}
      {(hasActive || hasDrafts) && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="my-registrations-banner">
          <h3 className="text-sm font-extrabold text-[#2B3A67] mb-3 flex items-center gap-2">
            <i className="ph-bold ph-check-circle text-green-500"></i>
            Your Registrations
          </h3>
          <div className="space-y-2">
            {[...activeRegistrations, ...draftRegistrations].map(([role, status]) => (
              <div
                key={role}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border ${ROLE_COLORS[role] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
                data-testid={`my-reg-${role}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status] || 'bg-gray-400'}`}></span>
                  <span className="font-bold text-sm">{ROLE_LABELS[role] || role}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold opacity-80">
                    {STATUS_LABELS[status] || status}
                  </span>
                  {byo && role === 'player' && !['draft', 'rejected'].includes(status) && (
                    <button
                      onClick={() => navigate(`/events/${event.id}/register?role=team`)}
                      className="text-xs font-bold underline opacity-70 hover:opacity-100"
                      data-testid="my-team-link"
                    >
                      My team
                    </button>
                  )}
                  {status === 'draft' && (
                    <button
                      onClick={() => navigate(`/events/${event.id}/register`)}
                      className="text-xs font-bold underline opacity-70 hover:opacity-100"
                      data-testid={`continue-draft-${role}`}
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Nothing to register for in the app ── */}
      {regInfo && nothingOpen && !hasActive && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center" data-testid="registration-not-open">
          <i className="ph-bold ph-clipboard-text text-3xl text-gray-300 mb-2 block"></i>
          <p className="font-bold text-[#2B3A67]">Registration isn't open in the app for this tournament</p>
          <p className="text-sm text-gray-500 mt-1">Contact the organiser to take part.</p>
        </div>
      )}

      {/* ── Fully Registered Message ── */}
      {fullyDone && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200 text-center" data-testid="fully-registered-msg">
          <i className="ph-bold ph-seal-check text-4xl text-green-500 mb-2 block"></i>
          <p className="font-extrabold text-green-800">You're registered for all available roles!</p>
          <p className="text-sm text-green-600 mt-1">Check your profile for registration status updates.</p>
        </div>
      )}

      {/* ── Registration Card ── shown only when there are roles left to register for */}
      {canRegMore && (
        <div className="bg-gradient-to-br from-[#2B3A67] to-[#1e2a4a] rounded-2xl p-6 text-white">
          <h2 className="text-xl font-extrabold mb-2">
            {hasActive && !byo ? 'Register for Another Role' : 'Register for the Tournament'}
          </h2>
          <p className="text-white/70 text-sm mb-4">
            {phase.description || (byo
              ? (offered.includes('sponsor')
                ? 'Register your team and invite your players with a link — or sponsor the tournament.'
                : 'Register your team and invite your players with a link.')
              : `Register as a ${listRoles(ALL_ROLES)} to take part in the tournament.`)}
          </p>

          {/* Fee chips */}
          {event.cricket_config && (
            <div className="flex flex-wrap gap-2 mb-4">
              {byo && (
                <span className="bg-white/15 px-3 py-1.5 rounded-lg text-sm" data-testid="byo-fee-chip">
                  Team: {event.cricket_config.team_fee > 0
                    ? `₹${(event.cricket_config.team_fee / 100).toLocaleString()}`
                    : 'Free'}
                  {event.cricket_config.player_fee > 0 && ` or ₹${(event.cricket_config.player_fee / 100).toLocaleString()} per player`}
                </span>
              )}
              {!byo && unregisteredRoles.includes('player') && event.cricket_config.player_fee >= 0 && (
                <span className="bg-white/15 px-3 py-1.5 rounded-lg text-sm">
                  Player: {event.cricket_config.player_fee > 0
                    ? `₹${(event.cricket_config.player_fee / 100).toLocaleString()}`
                    : 'Free'}
                </span>
              )}
              {!byo && unregisteredRoles.includes('team_owner') && event.cricket_config.team_owner_fee >= 0 && (
                <span className="bg-white/15 px-3 py-1.5 rounded-lg text-sm">
                  Team Owner: {event.cricket_config.team_owner_fee > 0
                    ? `₹${(event.cricket_config.team_owner_fee / 100).toLocaleString()}`
                    : 'Free'}
                </span>
              )}
              {unregisteredRoles.includes('sponsor') && event.cricket_config.sponsor_fee >= 0 && (
                <span className="bg-white/15 px-3 py-1.5 rounded-lg text-sm">
                  Sponsor: {event.cricket_config.sponsor_fee > 0
                    ? `₹${(event.cricket_config.sponsor_fee / 100).toLocaleString()}`
                    : 'Free'}
                </span>
              )}
            </div>
          )}

          <button
            onClick={() => navigate(`/events/${event.id}/register`)}
            className="w-full py-3 rounded-xl bg-[#F4D341] text-[#2B3A67] font-extrabold text-sm hover:bg-[#e6c73a] transition-colors"
            data-testid="register-now-btn"
          >
            {hasActive && !byo ? 'Register for Another Role' : 'Register Now'}
          </button>
        </div>
      )}

      {/* Rules PDF */}
      {phase.rules_pdf_url && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <i className="ph-bold ph-file-pdf text-red-500"></i>
            Registration Rules
          </h3>
          <a
            href={phase.rules_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B3A67] text-white rounded-lg text-sm font-semibold hover:bg-[#1e2a4a] transition-colors"
            data-testid="register-rules-pdf-link"
          >
            <i className="ph-bold ph-download-simple"></i>
            Download PDF
          </a>
        </div>
      )}
    </div>
  );
}

// Auction Phase - Now using the full Auction module
function AuctionPhaseWrapper({ phase, event }) {
  return <AuctionPhase phase={phase} event={event} />;
}

// Live Phase uses imported LivePhase component

// Results Phase uses imported ResultsPhase component from ../components/results

// Generic Phase (Fallback)
function GenericPhase({ phase }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="generic-phase">
      <h2 className="text-lg font-extrabold mb-2">{phase.title || phase.default_title}</h2>
      <p className="text-gray-500">{phase.description || phase.default_description}</p>
    </div>
  );
}
